import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { tokens } from '../Content/Calculations/tokens';
import './manage.css';

/**
 * Editorial Manage atoms — ported from the design
 * (subscription_redesigns/manage-variations.jsx > Manage_01_Chapters), retyped
 * and wired to the shared tokens. These are presentational; the page wires them
 * to the existing RTK mutations.
 */

export const Action = ({
  n,
  title,
  intro,
  accent,
  body,
}: {
  n: string;
  title: string;
  intro: string;
  accent: string;
  body: ReactNode;
}) => (
  <div style={{ padding: '48px 0', borderTop: tokens.borderFaint }}>
    <div
      style={{
        display: 'grid',
        // Narrower text column, wider form column — the intro is short copy,
        // the form holds the dropdowns/sliders/buttons that need the room.
        gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 9fr)',
        gap: 48,
        alignItems: 'flex-start',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            color: accent,
            fontFamily: tokens.fontMono,
            letterSpacing: '0.25em',
            marginBottom: 8,
          }}
        >
          ACTION · {n}
        </div>
        <div
          style={{
            fontSize: 40,
            fontFamily: tokens.fontDisplay,
            lineHeight: 0.95,
            letterSpacing: '-0.01em',
            color: tokens.textPrimary,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: tokens.textMuted,
            marginTop: 16,
            lineHeight: 1.6,
            maxWidth: 320,
          }}
        >
          {intro}
        </div>
      </div>
      <div>{body}</div>
    </div>
  </div>
);

// Mix a hex colour toward white by `amount` (0..1) — for button hover fills.
const lighten = (hex: string, amount = 0.18): string => {
  const stripped = hex.replace('#', '');
  const full =
    stripped.length === 3
      ? stripped
          .split('')
          .map((c) => c + c)
          .join('')
      : stripped;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return hex;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

export const EditorialButton = ({
  children,
  accent = tokens.textPrimary,
  dark = false,
  disabled = false,
  onClick,
}: {
  children: ReactNode;
  accent?: string;
  dark?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) => {
  const [hover, setHover] = useState(false);

  // Plain if/else (no nested ternaries) so the formatting is identical across
  // Prettier 2 and 3. Hover changes the background: transparent buttons fill
  // with a faint accent, filled (dark) buttons lighten.
  let background = 'transparent';
  let textColor = accent;
  if (disabled) {
    background = tokens.backgroundElevated;
    textColor = tokens.textFaint;
  } else if (dark) {
    background = hover ? lighten(accent) : accent;
    textColor = tokens.backgroundBase;
  } else if (hover) {
    background = `${accent}26`;
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="sub-editorial-btn"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '12px 28px',
        background,
        color: textColor,
        border: `1px solid ${disabled ? 'rgba(255,255,255,0.15)' : accent}`,
        fontSize: 13,
        fontFamily: tokens.fontMono,
        letterSpacing: '0.18em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textTransform: 'uppercase',
        fontWeight: 700,
      }}
    >
      {children}
    </button>
  );
};

export const StaticCell = ({
  label,
  value,
  accent = tokens.textMuted,
  sz = 28,
  valueColor = tokens.textPrimary,
}: {
  label: string;
  value: string;
  accent?: string;
  sz?: number;
  valueColor?: string;
}) => (
  <div
    style={{
      padding: '18px 22px',
      background: tokens.backgroundBase,
      borderTop: `2px solid ${accent}55`,
    }}
  >
    <div
      style={{
        fontSize: 10,
        color: accent,
        fontFamily: tokens.fontMono,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: sz,
        fontFamily: tokens.fontDisplay,
        color: valueColor,
        marginTop: 4,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
  </div>
);

const DropdownOption = ({
  option,
  accent,
  active,
  onSelect,
}: {
  option: string;
  accent: string;
  active: boolean;
  onSelect: () => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '12px 22px',
        fontSize: 20,
        fontFamily: tokens.fontDisplay,
        color: active ? accent : tokens.textPrimary,
        background: hover ? `${accent}22` : 'transparent',
        cursor: 'pointer',
        lineHeight: 1.1,
      }}
    >
      {option}
    </div>
  );
};

// Fully custom editorial dropdown — no native <select>. Click toggles a styled
// popover; a transparent backdrop closes it on outside click.
export const DropdownCell = ({
  label,
  value,
  options,
  onChange,
  accent = tokens.green,
  sz = 32,
  disabled = false,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
  accent?: string;
  sz?: number;
  disabled?: boolean;
}) => {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const interactive = !disabled && options.length > 1;

  return (
    <div style={{ position: 'relative' }}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => interactive && setOpen((value) => !value)}
        style={{
          padding: '18px 22px',
          background:
            (hover || open) && interactive ? `${accent}10` : `${accent}05`,
          borderTop: `2px solid ${accent}`,
          cursor: interactive ? 'pointer' : 'default',
          transition: 'background 0.15s',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: accent,
              fontFamily: tokens.fontMono,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </div>
          {interactive && (
            <span
              style={{
                fontSize: 11,
                color: accent,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.15em',
              }}
            >
              CLICK TO CHANGE
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginTop: 4,
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: sz,
              fontFamily: tokens.fontDisplay,
              color: tokens.textPrimary,
              lineHeight: 1.1,
            }}
          >
            {value}
          </span>
          <span
            style={{
              fontSize: 20,
              color: accent,
              lineHeight: 1,
              fontFamily: tokens.fontMono,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          >
            ▾
          </span>
        </div>
      </div>

      {open && interactive && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 41,
              marginTop: 2,
              background: tokens.backgroundRaised,
              border: `1px solid ${accent}`,
              maxHeight: 240,
              overflowY: 'auto',
              boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
            }}
          >
            {options.map((option) => (
              <DropdownOption
                key={option}
                option={option}
                accent={accent}
                active={option === value}
                onSelect={() => {
                  onChange(option);
                  setOpen(false);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const DeltaInput = ({
  label,
  current,
  currentNum,
  unit,
  delta,
  onDelta,
  accent = tokens.green,
  decimals = 2,
  step = 1,
}: {
  label: string;
  /** Current value, already formatted for display. */
  current: string;
  /** Current value as a number — used to compute the previewed "new" value. */
  currentNum: number;
  unit: string;
  delta: number;
  onDelta: (next: number) => void;
  accent?: string;
  decimals?: number;
  step?: number;
}) => {
  const round = (value: number) => Number(value.toFixed(decimals));
  const format = (value: number) =>
    (value >= 0 ? '+' : '') + String(value).replace('.', ',');

  // Local text buffer so intermediate input ("-", "-5", a typed "500" you then
  // make negative) survives — a value bound straight to `delta` would erase a
  // lone "-" before the digits arrive, making negatives impossible to type.
  const [text, setText] = useState(() => format(delta));
  const internalDelta = useRef(delta);
  useEffect(() => {
    if (delta !== internalDelta.current) {
      internalDelta.current = delta;
      setText(format(delta));
    }
  }, [delta]);

  const commit = (next: number) => {
    internalDelta.current = next;
    onDelta(next);
  };
  const handleChange = (raw: string) => {
    setText(raw);
    const parsed = parseFloat(raw.replace(',', '.'));
    commit(isNaN(parsed) ? 0 : round(parsed));
  };
  const bump = (amount: number) => {
    const next = round(delta + amount);
    setText(format(next));
    commit(next);
  };

  const nextLabel =
    delta === 0
      ? null
      : (currentNum + delta).toFixed(decimals).replace('.', ',');

  let inputColor: string = tokens.red;
  if (delta === 0) inputColor = tokens.textMuted;
  else if (delta > 0) inputColor = tokens.green;

  return (
    <div
      style={{
        padding: '18px 0',
        background: tokens.backgroundBase,
        borderTop: `2px solid ${accent}55`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: accent,
            fontFamily: tokens.fontMono,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
        {delta === 0 ? (
          <div
            style={{
              fontSize: 11,
              color: tokens.textFaint,
              fontFamily: tokens.fontMono,
              letterSpacing: '0.08em',
            }}
          >
            current{' '}
            <span style={{ color: tokens.textSecondary, fontWeight: 600 }}>
              {current} {unit}
            </span>
          </div>
        ) : (
          <div
            style={{
              fontSize: 11,
              color: accent,
              fontFamily: tokens.fontMono,
              letterSpacing: '0.08em',
            }}
          >
            new{' '}
            <span style={{ color: tokens.textPrimary, fontWeight: 600 }}>
              {nextLabel} {unit}
            </span>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'stretch',
          border: `1px solid ${
            delta !== 0 ? accent : 'rgba(255,255,255,0.18)'
          }`,
          background: '#050505',
        }}
      >
        <button
          onClick={() => bump(-step)}
          style={{
            width: 32,
            background: 'transparent',
            color: tokens.textSecondary,
            border: 'none',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            fontSize: 18,
            cursor: 'pointer',
            fontFamily: tokens.fontMono,
          }}
        >
          −
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={() => setText(format(delta))}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            color: inputColor,
            fontFamily: tokens.fontDisplay,
            fontSize: 26,
            outline: 'none',
            padding: '4px 10px',
            letterSpacing: '-0.01em',
            textAlign: 'center',
            minWidth: 0,
            lineHeight: 1.1,
          }}
        />
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            color: tokens.textFaint,
            fontFamily: tokens.fontMono,
            fontSize: 11,
            letterSpacing: '0.15em',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            borderRight: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {unit}
        </span>
        <button
          onClick={() => bump(step)}
          style={{
            width: 32,
            background: 'transparent',
            color: tokens.textSecondary,
            border: 'none',
            fontSize: 18,
            cursor: 'pointer',
            fontFamily: tokens.fontMono,
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};

export const FlagToggle = ({
  on,
  color,
  onChange,
  size = 18,
}: {
  on: boolean;
  color: string;
  onChange: (next: boolean) => void;
  size?: number;
}) => (
  <div
    onClick={() => onChange(!on)}
    style={{
      width: size,
      height: size,
      border: `1.5px solid ${color}`,
      background: on ? color : 'transparent',
      cursor: 'pointer',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.backgroundBase,
      fontWeight: 700,
      fontSize: size * 0.6,
      transition: 'background 0.12s',
    }}
  >
    {on && '✓'}
  </div>
);

export const EditorialCheck = ({
  checked,
  onChange,
  color = tokens.amber,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  color?: string;
  children: ReactNode;
}) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 18,
        height: 18,
        border: `1.5px solid ${color}`,
        marginTop: 3,
        flexShrink: 0,
        background: checked ? color : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        color: '#000',
      }}
    >
      {checked && '✓'}
    </div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export const InfoBanner = ({
  children,
  accent = tokens.amber,
}: {
  children: ReactNode;
  accent?: string;
}) => (
  <div
    style={{
      marginTop: 16,
      padding: '10px 14px',
      fontSize: 12,
      color: accent,
      fontFamily: tokens.fontMono,
      background: `${accent}10`,
      borderLeft: `2px solid ${accent}`,
      lineHeight: 1.5,
    }}
  >
    {children}
  </div>
);

export const cellGrid: CSSProperties = {
  display: 'grid',
  // Responsive: two columns when there's room, stacks on narrow widths.
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 4,
};
